import React from "react"
import { useTranslation } from "react-i18next"

import { SelectOption } from "shared/types/Select"
import { Button, ButtonVariant } from "shared/ui/buttons/Button"
import { OverlayHeader } from "shared/ui/overlay/OverlayHeader"
import { OverlayWrapper } from "shared/ui/overlay/OverlayWrapper"

import { TeamRole } from "entities/team-members/types/TeamRole"
import {
  TeamMemberList,
  TeamMemberListProps
} from "entities/team-members/ui/team-member-list/TeamMemberList"
import { Team } from "entities/teams/types/Team"
import {
  CreateSingleTeamMemberForm,
  CreateTeamMemberFormModel
} from "features/team-member-management/ui/create-team-member-form"

import "../styles/_manage-project-team-overlay.scss"

export interface ManageProjectTeamOverlayViewModel {
  team: Team | null | undefined
  isLoadingTeam?: boolean

  formModel: CreateTeamMemberFormModel
  isSubmitting?: boolean

  roleOptions: SelectOption<TeamRole>[]

  onResendInvitation?: TeamMemberListProps["onResendInvitation"]
  onUpdateTeamMember?: TeamMemberListProps["onUpdate"]
  onDeleteTeamMember?: TeamMemberListProps["onDelete"]

  onInviteMore?: () => void
}

export const ManageProjectTeamOverlayView = React.memo(
  ({
    team,
    formModel,
    isSubmitting,
    onUpdateTeamMember,
    onResendInvitation,
    onDeleteTeamMember,
    onInviteMore
  }: ManageProjectTeamOverlayViewModel) => {
    const { t } = useTranslation()

    return (
      <OverlayWrapper
        id="manage-team-overlay"
        classNamePrefix="manage-project-team-overlay"
        prefix="manage-team-overlay"
        mobileFullscreen
      >
        <OverlayHeader.Container>
          <OverlayHeader.Title>
            {t("ProjectTeam.Actions.Edit")}
          </OverlayHeader.Title>
          <OverlayHeader.CloseButton className="primary" />
        </OverlayHeader.Container>

        <div className="manage-project-team-overlay__content overlay-scroll-container">
          <div className="manage-project-team-overlay__invite-form">
            <h3 className="label">{t("Project.Actions.InviteTeamMembers")}</h3>
            <CreateSingleTeamMemberForm
              {...formModel}
              id="manage-project-team-overlay-invite-form"
              onInviteMore={onInviteMore}
            />
            <Button
              variant={ButtonVariant.PRIMARY}
              type="submit"
              form="manage-project-team-overlay-invite-form"
              className="manage-project-team-overlay__submit-invite-button"
              isLoading={isSubmitting}
            >
              {t("Project.Actions.SendInvite")}
            </Button>
          </div>
          <div className="manage-project-team-overlay__member-list">
            <h3 className="label">{t("ProjectTeam.CurrentPermissions")}</h3>
            <TeamMemberList
              teamMembers={team?.memberships}
              onResendInvitation={onResendInvitation}
              onUpdate={onUpdateTeamMember}
              onDelete={onDeleteTeamMember}
            />
          </div>
        </div>
      </OverlayWrapper>
    )
  }
)
